import React, { useMemo } from 'react';
import propTypes from 'prop-types';
import { NavLink, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
;
import { UiTable } from 'src/ui';
import { Button, MuiDropdown, MuiDropdownItem } from 'src/ui/mui-ui-kit';;
import { find } from 'lodash';
import dayjs from 'dayjs-ext';
import { COVERAGE_TYPE } from '../const';
import { DATE_SMONTH_DAY_SYEAR } from '../../ui/const';
import { startsWithCaseInsensitive } from '../../ui';

export function InsurancePeopleTable({
  people,
  showActionsDropdown,
  loading,
  paging,
  onDeletePerson,
  onRunBackgroundChecks,
  onResetPassword,
  onSendInvite,
  hideNoDataComponent,
  onApprove,
  onDeny,
  ...props
}) {
  /* eslint-disable react/prop-types */
  const tableColumns = useMemo(() => {
    const result = [
      {
        Header: () => (
          <span className="icon is-small table-header-member-icon">
            <FontAwesomeIcon icon={['fas', 'user-circle']} size="2x" />
          </span>
        ),
        Cell: ({ original }) => (
          <div className={`person-status ${original.statusClassName}`}>
            <span className="icon">
              <FontAwesomeIcon icon={['fas', 'user-circle']} size="2x" />
              {original.isAdmin && (
                <span className="icon-tag">
                  <FontAwesomeIcon icon={['fas', 'star']} />
                </span>
              )}
            </span>
          </div>
        ),
        sortable: false,
        width: 65,
        resizable: false,
        filterable: false,
      },
      {
        Header: 'Person',
        accessor: 'name',
        filterMethod: startsWithCaseInsensitive,
        Cell: ({ original, value }) => (
          <div className="member-name">
            <div>
              <Link className="edit-link" to={`/people/edit/${original.id}`}>
                {value}
              </Link>
            </div>
            <small className="has-text-grey-medium-dark">
              {original.title && `${original.title} | `}
              {original.emailAddress}
            </small>
          </div>
        ),
      },
      {
        Header: 'Role',
        accessor: 'roleName',
        Cell: row => (
          <div className="has-text-centered">
            {row.value === 'Merchant - Admin' ||
            row.value === 'Merchant - Primary Admin'
              ? 'Admin'
              : 'User'}
          </div>
        ),
        filterable: false,
      },

      {
        Header: 'Coverage Type',
        accessor: 'insuranceInformation.coverage',
        Cell: row => (
          <div className="has-text-centered">
            {row.value
              ? find(COVERAGE_TYPE, {
                  value: row.value,
                }).label
              : null}
          </div>
        ),
        filterable: false,
      },
      {
        Header: 'Group',
        accessor: 'insuranceInformation.groupName',
        filterMethod: (filter, row) => {
          return row[filter.id]
            ?.toUpperCase()
            .startsWith(filter.value.toUpperCase());
        },
        Cell: row => <div className="has-text-centered">{row.value}</div>,
      },
      {
        Header: 'Family Size',
        accessor: 'insuranceInformation.familyMembers',
        Cell: row => (
          <div className="has-text-centered">
            {row.value ? row.value.length + 1 : 1}
          </div>
        ),
        width: 150,
        filterable: false,
      },
      {
        Header: 'Last Submitted',
        accessor: 'insuranceInformation.lastSubmit',
        Cell: row => (
          <div className="has-text-centered">
            {row.value ? dayjs(row.value).format(DATE_SMONTH_DAY_SYEAR) : null}
          </div>
        ),
        width: 150,
        filterable: false,
      },
      {
        Header: 'User Marked Complete',
        accessor: 'insuranceInformation.insuranceInformationMarkedComplete',
        Cell: row => (
          <div className="has-text-centered">
            {row.value ? (
              <FontAwesomeIcon icon={['fal', 'check']} color="green" />
            ) : (
              <FontAwesomeIcon icon={['fal', 'minus-circle']} color="red" />
            )}
          </div>
        ),
        width: 150,
        filterable: false,
      },
    ];

    if (showActionsDropdown) {
      result.push({
        Header: '',
        accessor: 'id',
        sortable: false,
        width: 62,
        resizable: false,
        filterable: false,
        Cell: row => (
          <MuiDropdown
            isAlign="right"
            renderTrigger={
              <Button className="transparent-elipses">
                <span className="icon">
                  <FontAwesomeIcon
                    icon={['fal', 'ellipsis-v']}
                    className="has-text-grey"
                    size="lg"
                  />
                </span>
              </Button>
            }
          >
              <MuiDropdownItem tag={NavLink} to={`/people/edit/${row.value}`}>
                Edit
              </MuiDropdownItem>
              {onResetPassword && (
                <MuiDropdownItem
                  tag="a"
                  onClick={() => onResetPassword(row.original)}
                >
                  Reset password
                </MuiDropdownItem>
              )}
              {onSendInvite && (
                <MuiDropdownItem
                  tag="a"
                  onClick={() => onSendInvite(row.original)}
                >
                  Send invite
                </MuiDropdownItem>
              )}
              <MuiDropdownItem
                tag="a"
                onClick={() => onDeletePerson(row.original)}
              >
                Archive
              </MuiDropdownItem>
          </MuiDropdown>
        ),
      });
    }

    return result;
  }, [showActionsDropdown, onDeletePerson]);
  /* eslint-enable */

  return (
    <UiTable
      className="people-table"
      columns={tableColumns}
      getTheadThProps={(_state, _rowInfo, column) => ({
        className: column.id === 'name' ? 'has-text-left' : '',
      })}
      loading={loading}
      data={people}
      paging={paging}
      title="Person"
      {...props}
      // resolveData={resolveData}
      resolveData={data =>
        data.map(t => ({
          ...t,
        }))
      }
      hidePrev
      hideNext
      filterable
      hideNoDataComponent={hideNoDataComponent}
    />
  );
}

InsurancePeopleTable.defaultProps = {
  showActionsDropdown: false,
  loading: false,
  onDeletePerson: () => {},
  onRunBackgroundChecks: null,
  onResetPassword: null,
  onSendInvite: null,
  hideNoDataComponent: false,
  onApprove: null,
  onDeny: null,
};

InsurancePeopleTable.propTypes = {
  showActionsDropdown: propTypes.bool,
  loading: propTypes.bool,
  people: propTypes.array.isRequired,
  onDeletePerson: propTypes.func,
  onRunBackgroundChecks: propTypes.func,
  onResetPassword: propTypes.func,
  onSendInvite: propTypes.func,
  paging: propTypes.object.isRequired,
  hideNoDataComponent: propTypes.bool,
  onApprove: propTypes.func,
  onDeny: propTypes.func,
};
